import BottomSheet, { BottomSheetView } from '@gorhom/bottom-sheet';
import { Slider } from '@miblanchard/react-native-slider';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { router, Stack, useLocalSearchParams } from 'expo-router';
import React, { useRef } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Reservation } from '@/api';
import { useCarChargerPortDoor } from '@/api/cars/use-car-charger-port-door';
import { useCarClimateCabin } from '@/api/cars/use-car-climate-cabin';
import { useCarClimateCabinSteeringWheel } from '@/api/cars/use-car-climate-cabin-steering-wheel';
import { useCarSecurity } from '@/api/cars/use-car-security';
import { useCarSecurityStorage } from '@/api/cars/use-car-security-storage';
import { useCarStateRefresh } from '@/api/cars/use-car-state-refresh';
import { useReservation } from '@/api/reservations/use-reservation';
import BackButton from '@/components/back-button';
import CardSwitch from '@/components/card-switch';
import { MapView } from '@/components/map-view';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Button, Card, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Car as CarIcon, Lock } from '@/ui/icons';
import { AppStore } from '@/ui/icons/app-store';
import { ArrowClockwise } from '@/ui/icons/arrow-clockwise';
import { Bolt } from '@/ui/icons/bolt';
import { EvPlug } from '@/ui/icons/ev_plug';
import { Frunk } from '@/ui/icons/frunk';
import { GooglePlay } from '@/ui/icons/google-play';
import { SteeringWheel } from '@/ui/icons/steering-wheel';
import { Thermometer } from '@/ui/icons/thermometer';
import { Trunk } from '@/ui/icons/trunk';
import { Unlocked } from '@/ui/icons/unlocked';
import { Loader } from '@/ui/loader';

const SNAP_POINTS = [20, 60, 80];

export default function CarControlPanel() {
  const { styles, theme } = useStyles(stylesheet);
  const { id } = useLocalSearchParams();
  const bottomSheetRef = useRef<BottomSheet>(null);
  const [climateTemperature, setClimateTemperature] = React.useState(60);
  const queryClient = useQueryClient();
  const [bottomSheetIndex, setBottomSheetIndex] = React.useState(1);

  const {
    data: resData,
    isPending: isFetchingReservation,
    refetch: refetchTrip,
  } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: !!id,
  });

  const { mutateAsync: refreshCarState, isPending: isRefreshingCarState } =
    useCarStateRefresh();

  const { mutateAsync: setClimateCabin, isPending: isSettingClimateCabin } =
    useCarClimateCabin();

  const {
    mutateAsync: setClimateCabinSteeringWheel,
    isPending: isSettingClimateCabinSteeringWheel,
  } = useCarClimateCabinSteeringWheel();

  const { mutateAsync: setCarSecurity, isPending: isSettingCarSecurity } =
    useCarSecurity();

  const {
    mutateAsync: setCarChargerPortDoor,
    isPending: isSettingCarChargerPortDoor,
  } = useCarChargerPortDoor();

  const {
    mutateAsync: setCarSecurityStorage,
    isPending: isSettingCarSecurityStorage,
  } = useCarSecurityStorage();

  React.useEffect(() => {
    if (!resData) return;

    if (resData.car.carState.climateTemperature) {
      setClimateTemperature(resData.car.carState.climateTemperature);
    }
  }, [resData]);

  const handleStateRefresh = async () => {
    try {
      const carState = await refreshCarState({
        id: resData.car.id,
        reservationId: resData.id,
      });

      queryClient.setQueryData(['reservations/id'], (oldData: Reservation) => {
        return {
          ...oldData,
          carState: carState,
        };
      });

      refetchTrip();

      Toast.show({
        type: 'success',
        text1: 'Car data refreshed',
      });
    } catch (e) {
      Toast.show({
        text1: e.message,
        type: 'error',
      });
    }
  };

  const handleToggleClimateCabin = async () => {
    try {
      const carState = await setClimateCabin({
        id: resData.car.id,
        reservationId: resData.id,
        action: resData.car.carState.isClimateOn ? 'STOP' : 'START',
        temperature: climateTemperature,
      });

      queryClient.setQueryData(['reservations/id'], (oldData: Reservation) => {
        return {
          ...oldData,
          carState: carState,
        };
      });

      refetchTrip();

      Toast.show({
        text1: resData.car.carState.isClimateOn
          ? 'AC turned off'
          : 'AC turned on',
        type: 'success',
      });
    } catch (e) {
      Toast.show({
        text1: e.message,
        type: 'error',
      });
    }
  };

  const handleClimateCabin = async () => {
    try {
      const carState = await setClimateCabin({
        id: resData.car.id,
        reservationId: resData.id,
        action: 'SET',
        temperature: climateTemperature,
      });

      queryClient.setQueryData(['reservations/id'], (oldData: Reservation) => {
        return {
          ...oldData,
          carState: carState,
        };
      });

      refetchTrip();

      Toast.show({
        text1: `Temperature set to ${climateTemperature}°F`,
        type: 'success',
      });
    } catch (e) {
      Toast.show({
        text1: e.message,
        type: 'error',
      });
    }
  };

  const handleSteeringWheelHeater = async () => {
    try {
      const carState = await setClimateCabinSteeringWheel({
        id: resData.car.id,
        reservationId: resData.id,
        action: resData.car.carState.isSteeringWheelHeaterOn ? 'STOP' : 'START',
      });

      queryClient.setQueryData(['reservations/id'], (oldData: Reservation) => {
        return {
          ...oldData,
          carState: carState,
        };
      });

      refetchTrip();

      Toast.show({
        text1: resData.car.carState.isSteeringWheelHeaterOn
          ? 'Steering heater turned off'
          : 'Steering heater turned on',
        type: 'success',
      });
    } catch (e) {
      Toast.show({
        text1: e.message,
        type: 'error',
      });
    }
  };

  const handleCarSecurity = async () => {
    try {
      const carState = await setCarSecurity({
        id: resData.car.id,
        reservationId: resData.id,
        action: resData.car.carState.isLocked ? 'UNLOCK' : 'LOCK',
      });

      queryClient.setQueryData(
        ['reservations/id', id],
        (oldData: Reservation) => {
          return {
            ...oldData,
            carState: carState,
          };
        }
      );

      refetchTrip();

      Toast.show({
        text1: resData.car.carState.isLocked
          ? 'Car unlocked successfully'
          : 'Car locked securely',
        type: 'success',
      });
    } catch (e) {
      Toast.show({
        text1: e.message,
        type: 'error',
      });
    }
  };

  const handleChargerPortDoor = async () => {
    try {
      const carState = await setCarChargerPortDoor({
        id: resData.car.id,
        reservationId: resData.id,
        action:
          resData.car.carState.chargerPortStatus === 'OPEN' ? 'CLOSE' : 'OPEN',
      });

      queryClient.setQueryData(
        ['reservations/id', id],
        (oldData: Reservation) => {
          return {
            ...oldData,
            carState: carState,
          };
        }
      );

      refetchTrip();

      Toast.show({
        text1:
          resData.car.carState.chargerPortStatus === 'OPEN'
            ? 'Charger port closed'
            : 'Charger port opened',
        type: 'success',
      });
    } catch (e) {
      Toast.show({
        text1: e.message,
        type: 'error',
      });
    }
  };

  const handleTrunk = async () => {
    try {
      const carState = await setCarSecurityStorage({
        id: resData.car.id,
        reservationId: resData.id,
        type: 'TRUNK',
        action: resData.car.carState.trunkStatus === 'OPEN' ? 'CLOSE' : 'OPEN',
      });

      queryClient.setQueryData(
        ['reservations/id', id],
        (oldData: Reservation) => {
          return {
            ...oldData,
            carState: carState,
          };
        }
      );

      refetchTrip();

      Toast.show({
        text1:
          resData.car.carState.trunkStatus === 'OPEN'
            ? 'Trunk closed'
            : 'Trunk opened',
        type: 'success',
      });
    } catch (e) {
      Toast.show({
        text1: e.message,
        type: 'error',
      });
    }
  };

  const handleFrunk = async () => {
    try {
      const carState = await setCarSecurityStorage({
        id: resData.car.id,
        reservationId: resData.id,
        type: 'FRUNK',
        action: resData.car.carState.frunkStatus === 'OPEN' ? 'CLOSE' : 'OPEN',
      });

      queryClient.setQueryData(
        ['reservations/id', id],
        (oldData: Reservation) => {
          return {
            ...oldData,
            carState: carState,
          };
        }
      );

      refetchTrip();

      Toast.show({
        text1:
          resData.car.carState.frunkStatus === 'OPEN'
            ? 'Frunk closed'
            : 'Frunk opened',
        type: 'success',
      });
    } catch (e) {
      Toast.show({
        text1: e.message,
        type: 'error',
      });
    }
  };

  // const queryCache = queryClient.getQueryCache();
  // const queryKeys = queryCache.getAll().map((cache) => cache.queryKey);

  // console.log('queryKeys', queryKeys);

  if (Platform.OS === 'web') {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.background_1,
          alignItems: 'center',
          padding: 16,
        }}
      >
        <View style={styles.container}>
          {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
            <Stack.Screen
              options={{
                headerShown: false,
              }}
            />
          ) : (
            <Stack.Screen
              options={{
                title: 'Control Panel',
                headerTitle: 'Control Panel',
                headerLeft: () => <BackButton />,
                headerShown: true,
                headerStyle: {
                  backgroundColor: theme.colors.background_1,
                },
                headerTintColor: theme.colors.text.primary,
                headerShadowVisible: false,
                headerTitleStyle: {
                  fontSize: 17,
                },
              }}
            />
          )}

          {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
            <View style={{ gap: 16 }}>
              <NavigationButton
                type={NavigationButtonTypes.back_with_text}
                onPress={() => {
                  router.back();
                }}
              />

              <Text type="largeTitle">Control Panel</Text>
            </View>
          ) : null}

          <View style={{ gap: 32 }}>
            <View>
              <Text type="title1">
                Download the Eon app to get access to your control panel!
              </Text>
            </View>

            <View
              style={{
                alignSelf: 'center',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: 32,
              }}
            >
              <View style={{ alignItems: 'center' }}>
                <Image
                  source={
                    UnistylesRuntime.themeName === 'dark'
                      ? require('../../../../../../public/images/store/app-store-dark.png')
                      : require('../../../../../../public/images/store/app-store-light.png')
                  }
                  contentFit="contain"
                  style={{
                    width: 140,
                    height: 140,
                  }}
                />

                <Pressable
                  onPress={() => {
                    window.open(
                      'https://apps.apple.com/us/app/eon-instant-ev-rentals/id6448188911',
                      '_blank'
                    );
                  }}
                >
                  <Icon
                    type="naked"
                    icon={<AppStore />}
                    width={160}
                    height={90}
                  />
                </Pressable>
              </View>

              <View style={{ alignItems: 'center' }}>
                <Image
                  source={
                    UnistylesRuntime.themeName === 'dark'
                      ? require('../../../../../../public/images/store/google-play-dark.png')
                      : require('../../../../../../public/images/store/google-play-light.png')
                  }
                  contentFit="contain"
                  style={{
                    width: 140,
                    height: 140,
                  }}
                />

                <Pressable
                  onPress={() => {
                    window.open(
                      'https://play.google.com/store/apps/details?id=com.eon.mobile',
                      '_blank'
                    );
                  }}
                >
                  <Icon
                    type="naked"
                    icon={<GooglePlay />}
                    width={160}
                    height={90}
                  />
                </Pressable>
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  }

  if (isFetchingReservation) {
    return <Loader />;
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />

      <View style={{ flexGrow: 1, backgroundColor: theme.colors.background_1 }}>
        <View
          style={{
            width: '100%',
            height: `${100 - SNAP_POINTS[bottomSheetIndex]}%`,
            borderRadius: 16,
          }}
        >
          <MapView
            markers={[
              {
                latitude: resData.car.carState.lat,
                longitude: resData.car.carState.lon,
                icon: (
                  <Icon
                    type="naked"
                    icon={<CarIcon />}
                    fillColor={theme.colors.icon.color}
                    width={14}
                    height={14}
                    style={{ borderColor: 'red', borderWidth: 1 }}
                  />
                ),
              },
            ]}
            defaultCenter={{
              latitude: resData.car.carState.lat,
              longitude: resData.car.carState.lon,
            }}
            fitBounds={false}
            altitude={3000}
            zoom={16}
            showsUserLocation={false}
            style={{ borderRadius: 0 }}
          />
        </View>

        <BottomSheet
          ref={bottomSheetRef}
          index={1}
          snapPoints={SNAP_POINTS.map((point) => `${point}%`)}
          handleStyle={{
            backgroundColor: theme.colors.background_1,
          }}
          handleIndicatorStyle={{
            backgroundColor: theme.colors.text.secondary,
            width: 120,
          }}
          backgroundStyle={{
            backgroundColor: theme.colors.background_1,
          }}
          onChange={(index: number) => {
            setBottomSheetIndex(index);
          }}
        >
          <BottomSheetView
            style={{
              flexGrow: 1,
              backgroundColor: theme.colors.background_1,
              paddingBottom: 40,
            }}
          >
            {isRefreshingCarState ||
            isSettingClimateCabin ||
            isSettingClimateCabinSteeringWheel ||
            isSettingCarSecurity ||
            isSettingCarSecurityStorage ||
            isSettingCarChargerPortDoor ? (
              <Loader style={{ position: 'absolute' }} />
            ) : null}

            <View>
              <View
                style={{
                  borderBottomColor: theme.colors.card.default.borderColor,
                  borderBottomWidth: 1,
                }}
              >
                <View
                  style={{
                    height: 60,
                    padding: 16,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <View style={{ position: 'absolute', left: 16 }}>
                    <BackButton />
                  </View>

                  <Text type="title2">Control Panel</Text>
                </View>
              </View>

              <ScrollView
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{
                  flexGrow: 1,
                  padding: 16,
                  gap: 24,
                  paddingBottom: 250,
                }}
                style={{ flexGrow: 1 }}
              >
                <View style={{ gap: 8 }}>
                  <View
                    style={{
                      borderColor: theme.colors.card.default.borderColor,
                      borderWidth: 1,
                      borderRadius: 16,
                      padding: 16,
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <View style={{ justifyContent: 'center' }}>
                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        Data could take 15 minutes to update.
                      </Text>

                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`Last updated: ${format(
                          new Date(resData.car.carState.since),
                          'MM/dd/yyyy hh:mm a'
                        )}`}
                      </Text>
                    </View>

                    <Button
                      text="Refresh"
                      type="secondaryExtraSmall"
                      leftIcon={
                        <Icon
                          type="naked"
                          icon={<ArrowClockwise />}
                          fillColor={theme.colors.button.secondary.default.text}
                          width={12}
                          height={12}
                        />
                      }
                      onPress={handleStateRefresh}
                      style={{ width: 90 }}
                    />
                  </View>

                  <Card style={{ padding: 16, gap: 8 }}>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <Icon
                        type="dressed"
                        icon={<Bolt />}
                        fillColor={
                          resData.car.carState.chargeStatus === 'CHARGING'
                            ? theme.colors.success
                            : theme.colors.icon.color
                        }
                        width={24}
                        height={24}
                      />

                      <Text type="bodyRegular">
                        {resData.car.carState.chargeStatus === 'CHARGING'
                          ? 'Charging in progress'
                          : 'Battery & Range'}
                      </Text>
                    </View>

                    {/* <View>
                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        Range Remaining
                      </Text>

                      <Text type="title1">{`${resData.car.carState.estimatedRemainingBatteryRange} miles`}</Text>
                    </View> */}

                    <View>
                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        Percentage Remaining
                      </Text>

                      <Text type="title1">{`${resData.car.carState.usableBatteryLevel}%`}</Text>
                    </View>

                    <View
                      style={{
                        height: 16,
                        backgroundColor: theme.colors.card.default.borderColor,
                        borderRadius: 100,
                      }}
                    >
                      <View
                        style={{
                          height: '100%',
                          maxWidth: '100%',
                          width: `${resData.car.carState.usableBatteryLevel}%`,
                          borderRadius: 100,
                          backgroundColor: theme.colors.success,
                        }}
                      />
                    </View>
                  </Card>
                </View>

                <View style={{ gap: 16 }}>
                  <Text type="bodyRegular">Climate controls</Text>

                  <View style={{ gap: 8 }}>
                    <Card style={{ padding: 16, gap: 8 }}>
                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <Icon
                            type="dressed"
                            icon={<Thermometer />}
                            fillColor={theme.colors.icon.color}
                            width={24}
                            height={24}
                          />

                          <Text type="bodyRegular">Climate</Text>
                        </View>

                        <Button
                          text={
                            resData.car.carState.isClimateOn
                              ? 'Turn Off'
                              : 'Turn On'
                          }
                          type={
                            resData.car.carState.isClimateOn
                              ? 'primaryExtraSmall'
                              : 'secondaryExtraSmall'
                          }
                          onPress={handleToggleClimateCabin}
                          style={{ width: 90 }}
                        />
                      </View>

                      <View>
                        <Text
                          type="caption1"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          Current Interior Temperature
                        </Text>

                        <Text type="title1">
                          {`${resData.car.carState.interiorTemperature}°F`}
                        </Text>
                      </View>

                      <View>
                        <Text
                          type="caption1"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          Climate Temperature
                        </Text>

                        <Text type="title1">{`${climateTemperature}°F`}</Text>
                      </View>

                      <View>
                        <LinearGradient
                          start={{ x: 0, y: 0 }} // Gradient start point
                          end={{ x: 1, y: 0 }}
                          colors={[
                            'rgba(133, 181, 255, 1)',
                            'rgba(255, 255, 255, 1)',
                            'rgba(255, 161, 38, 1)',
                          ]}
                          style={{
                            borderRadius: 20,
                            height: 20,
                            justifyContent: 'center',
                          }}
                        >
                          <Slider
                            value={climateTemperature}
                            onSlidingComplete={(values) => {
                              setClimateTemperature(values[0]);
                            }}
                            minimumValue={60}
                            maximumValue={80}
                            step={1}
                            minimumTrackTintColor={'transparent'}
                            maximumTrackTintColor={'transparent'}
                            thumbStyle={{
                              backgroundColor: 'white',
                              borderColor: 'black',
                              borderRadius: 10,
                              borderWidth: 0.5,
                              height: 20,
                              width: 20,
                            }}
                          />
                        </LinearGradient>
                      </View>

                      <View style={{ alignItems: 'flex-end' }}>
                        <Button
                          text="Update"
                          type="primaryExtraSmall"
                          onPress={handleClimateCabin}
                          style={{ width: 90 }}
                          disabled={
                            climateTemperature ===
                            resData.car.carState.climateTemperature
                          }
                        />
                      </View>
                    </Card>

                    <CardSwitch
                      icon={<SteeringWheel />}
                      iconText="Steering Heater"
                      caption="Tap to turn"
                      title={
                        resData.car.carState.isSteeringWheelHeaterOn
                          ? 'Off'
                          : 'On'
                      }
                      onPress={handleSteeringWheelHeater}
                      active={resData.car.carState.isSteeringWheelHeaterOn}
                    />
                  </View>
                </View>

                <View style={{ gap: 16 }}>
                  <Text type="bodyRegular">Car controls</Text>

                  <View
                    style={{
                      gap: 8,
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                    }}
                  >
                    <CardSwitch
                      icon={
                        resData.car.carState.isLocked ? <Lock /> : <Unlocked />
                      }
                      iconText={
                        resData.car.carState.isLocked
                          ? 'Car locked'
                          : 'Car unlocked'
                      }
                      caption="Tap to"
                      title={resData.car.carState.isLocked ? 'Unlock' : 'Lock'}
                      onPress={handleCarSecurity}
                      active={resData.car.carState.isLocked}
                      style={{ width: '48%' }}
                    />

                    <CardSwitch
                      icon={<EvPlug />}
                      iconText="Charger Port"
                      caption="Tap to"
                      title={
                        resData.car.carState.chargerPortStatus === 'OPEN'
                          ? 'Close'
                          : 'Open'
                      }
                      onPress={handleChargerPortDoor}
                      active={resData.car.carState.chargerPortStatus === 'OPEN'}
                      style={{ width: '48%' }}
                    />

                    <CardSwitch
                      icon={<Trunk />}
                      iconText="Trunk"
                      caption="Tap to"
                      title={
                        resData.car.carState.trunkStatus === 'OPEN'
                          ? 'Close'
                          : 'Open'
                      }
                      onPress={handleTrunk}
                      active={resData.car.carState.trunkStatus === 'OPEN'}
                      style={{ width: '48%' }}
                    />

                    <CardSwitch
                      icon={<Frunk />}
                      iconText="Frunk"
                      caption="Tap to"
                      title={
                        resData.car.carState.frunkStatus === 'OPEN'
                          ? 'Close'
                          : 'Open'
                      }
                      onPress={handleFrunk}
                      active={resData.car.carState.frunkStatus === 'OPEN'}
                      style={{ width: '48%' }}
                    />
                  </View>
                </View>
              </ScrollView>
            </View>
          </BottomSheetView>
        </BottomSheet>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.background_1,
    justifyContent: {
      xs: 'space-between',
      md: 'flex-start',
    },
    gap: 16,
    width: {
      md: 600,
    },
    height: {
      md: 600,
    },
  },
}));
